import { useEffect, useState } from "react";

export interface InvitationData {
  invitee?: string;
  persons?: number;
}

/**
 * Convert URL-safe base64 to regular base64 and pad with '=' if needed.
 */
function base64UrlToBase64(input: string): string {
  let b64 = input.replace(/-/g, "+").replace(/_/g, "/");
  // pad with '=' to make length a multiple of 4
  while (b64.length % 4 !== 0) {
    b64 += "=";
  }
  return b64;
}

/**
 * Decode base64 into a UTF-8 string (handles Unicode properly).
 * Source pattern from MDN: decodes Base64 -> percent-encoded -> decodeURIComponent
 */
function base64DecodeUnicode(b64: string): string {
  // atob gives binary string (latin1), then convert to percent-encoding and decodeURIComponent
  try {
    const binary = atob(b64);
    const percentEncoded = binary
      .split("")
      .map((c) => {
        const code = c.charCodeAt(0).toString(16).toUpperCase();
        return "%" + (code.length < 2 ? "0" + code : code);
      })
      .join("");
    return decodeURIComponent(percentEncoded);
  } catch (err) {
    // fall back to returning the atob result (may be okay for ASCII)
    try {
      return atob(b64);
    } catch {
      return "";
    }
  }
}
// www.google.com?data=RmFtaWxpYSBSYWR5IFNhbm1pZ3VlbCw0

export const useInvitationParams = (): InvitationData => {
  const [params, setParams] = useState<InvitationData>({});

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    // Priority: if `data` param exists, decode it (expected format: "Name,Persons")
    const dataRaw = searchParams.get("data");
    if (dataRaw) {
      try {
        const fixedB64 = base64UrlToBase64(dataRaw);
        const decoded = base64DecodeUnicode(fixedB64).trim();

        // Split using last comma to allow commas in names (name may include commas rarely)
        const lastCommaIndex = decoded.lastIndexOf(",");
        if (lastCommaIndex === -1) {
          // If no comma found, fallback: put entire decoded into invitee
          setParams({ invitee: decoded });
          return;
        }

        const name = decoded.slice(0, lastCommaIndex).trim();
        const personsStr = decoded.slice(lastCommaIndex + 1).trim();
        const personsNum = personsStr ? Number(personsStr) : undefined;

        setParams({
          invitee: name || undefined,
          persons: Number.isFinite(personsNum) ? personsNum : undefined,
        });
        return;
      } catch (err) {
        console.error("Failed decoding `data` param:", err);
        // fall through to fallback parsing below
      }
    }

    // Fallback: separate query params invitee & persons
    const inviteeRaw = searchParams.get("invitee") ?? undefined;
    const personsRaw = searchParams.get("persons") ?? undefined;

    const invitee = inviteeRaw ? decodeURIComponent(inviteeRaw) : undefined;
    const persons = personsRaw ? Number(personsRaw) : undefined;

    setParams({
      invitee,
      persons: Number.isFinite(persons as number) ? (persons as number) : undefined,
    });
  }, []);

  return params;
};
